import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import AttributesTab from './AttributesTab';
import LayoutTab from './LayoutTab';
import './NewPortalPage.css';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    createPortalPage,
    readPortalPage,
    updatePortalPage,
    deletePortalPage,
    discardDraft,
    promoteDraft,
} from '../../services/portalPageService';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { SecurityAttributes } from '../../utils/data/enums';
import { useAuthContext } from '../../store/AuthContext';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import {
    createContainer,
    deleteContainer,
    updateContainer,
} from '../../services/containerService';
import PublishTab from './PublishTab';
import PreviewTab from './PreviewTab';
import { ROUTES } from '../../constants/routes';
import { CompactDropdown } from '../helpers/CompactDropdown';
import { hasContainerOverlap } from '../../utils/overlapUtils';
import { getText } from '../../i18n';

const NewPortalScreen = () => {
    const navigate = useNavigate();

    const location = useLocation();

    const { showModal } = useContext(ModalContext);

    const { user, hasAttribute } = useAuthContext();
    const { isAdminAudienceEnabled } = useAdminAudienceEnabled();

    const [savedPage, setSavedPage] = useState({});

    const [originalPage, setOriginalPage] = useState({});

    const [isDirty, setIsDirty] = useState(false);

    const [isLoading, setIsLoading] = useState(true);

    const SIDE_NAV_BAR_WIDTH = 130;
    const [resolution, setResolution] = useState(1366 - SIDE_NAV_BAR_WIDTH); // State for selected resolution

    const [fetchedContainers, setFetchedContainers] = useState([]);

    // Always compute overlap from latest containers
    const checkForOverlap = useCallback(() => {
        return hasContainerOverlap(fetchedContainers || []);
    }, [fetchedContainers]);

    const [savedGrid, setSavedGrid] = useState([]);

    const [savedMobileLayout, setSavedMobileLayout] = useState([]);

    const [fetchedMobileConfiguration, setFetchedMobileConfiguration] =
        useState([]);

    const [livePage, setLivePage] = useState(null);
    const [hasDraft, setHasDraft] = useState(false);
    const [draftHadPublishDate, setDraftHadPublishDate] = useState(false);
    const [hasOverlap, setHasOverlap] = useState(false);

    const isActivePage = savedPage.status === 1;
    const isDraftPage = Boolean(savedPage.draftOfPortalId);

    useEffect(() => {
        // Load Portal Page to pre-populate fields with existing data if editing an existing Portal Page, otherwise fields will be empty
        if (location.state && location.state.id) {
            loadPortalPage();
        } else {
            setIsLoading(false);
        }
    }, []);

    async function loadPortalPage() {
        setIsLoading(true);
        try {
            let tenantId =
                location.state?.template && !location.state.template.tenantId
                    ? 0
                    : null;
            const fetchedPortalPage = await readPortalPage(
                savedPage.id || location.state.id,
                true,
                tenantId,
            );

            const { viewModels, containers } = fetchedPortalPage;
            const portalData = viewModels[0] || [];

            if (location.state && location.state.loadFromTemplate) {
                portalData.id = '';
                portalData.name = '';
                portalData.status = 3;
            }

            if (portalData.status === 1 && portalData.draft) {
                setLivePage({ ...portalData });
                const draftData = portalData.draft;
                setHasDraft(true);
                setDraftHadPublishDate(Boolean(draftData.publishDate));
                setSavedPage(draftData);
                setOriginalPage(draftData);
                if (draftData.containers) {
                    setFetchedContainers(draftData.containers);
                }
                if (draftData.mobileConfiguration) {
                    setFetchedMobileConfiguration(
                        draftData.mobileConfiguration,
                    );
                }
            } else if (portalData.draftOfPortalId) {
                setHasDraft(true);
                setDraftHadPublishDate(Boolean(portalData.publishDate));
                setSavedPage(portalData);
                setFetchedContainers(containers);
                setFetchedMobileConfiguration(portalData.mobileConfiguration);
                setOriginalPage(portalData);
                try {
                    const livePageResponse = await readPortalPage(
                        portalData.draftOfPortalId,
                        true,
                    );
                    setLivePage(livePageResponse.viewModels[0] || null);
                } catch (liveErr) {
                    console.error('Error fetching live portal page:', liveErr);
                }
            } else {
                if (portalData.status === 1) {
                    setLivePage({ ...portalData });
                }
                setHasDraft(false);
                setDraftHadPublishDate(false);
                setSavedPage(portalData);
                setFetchedContainers(containers);
                setFetchedMobileConfiguration(portalData.mobileConfiguration);
                setOriginalPage(portalData);
            }
        } catch (e) {
            console.error('Error fetching portal page:', e);
            Toast.error(getText('msg_3f967464317a'));
        } finally {
            setIsLoading(false);
        }
    }

    async function refreshContainers() {
        try {
            const fetchedPortalPage = await readPortalPage(
                savedPage.id || location.state.id,
                true,
            );
            const { viewModels, containers } = fetchedPortalPage;
            const portalData = viewModels[0] || [];
            setFetchedContainers(containers);
            setFetchedMobileConfiguration(portalData.mobileConfiguration);
        } catch (e) {
            console.error('Error fetching portal page:', e);
        }
    }

    useEffect(() => {
        const normalizeArray = arr =>
            Array.isArray(arr) ? arr : arr ? [arr] : [];

        const normalizeValue = val =>
            val === null || val === undefined ? '' : String(val);

        const compareArrays = (arr1, arr2) => {
            const normalized1 = [...normalizeArray(arr1)].sort();
            const normalized2 = [...normalizeArray(arr2)].sort();
            return JSON.stringify(normalized1) === JSON.stringify(normalized2);
        };

        const hasChanges =
            normalizeValue(savedPage.name) !==
                normalizeValue(originalPage.name) ||
            normalizeValue(savedPage.displayName) !==
                normalizeValue(originalPage.displayName) ||
            normalizeValue(savedPage.description) !==
                normalizeValue(originalPage.description) ||
            !compareArrays(
                savedPage.populationGroups,
                originalPage.populationGroups,
            ) ||
            normalizeValue(savedPage.parentId) !==
                normalizeValue(originalPage.parentId) ||
            Boolean(savedPage.isChild) !== Boolean(originalPage.isChild) ||
            Boolean(savedPage.isAdditionalUserVisible) !==
                Boolean(originalPage.isAdditionalUserVisible) ||
            normalizeValue(savedPage.agencyId) !==
                normalizeValue(originalPage.agencyId) ||
            !compareArrays(
                savedPage.availableTierIds,
                originalPage.availableTierIds,
            );

        setIsDirty(hasChanges);
    }, [savedPage, originalPage]);

    //Programmatically change name of page (Attributes, Layout, Preview, Publish)
    //Programmatically highlight correct word on breadcrumbs
    const [wizardStep, setWizardStep] = useState(1);

    const [previewMode, setPreviewMode] = useState('desktop');

    const [previewSource, setPreviewSource] = useState('draft');

    const [layoutSource, setLayoutSource] = useState('draft');

    const renderPageTitle = () => {
        if (wizardStep === 1) {
            return getText('msg_a6652617f2c7');
        } else if (wizardStep === 2) {
            return getText('msg_6fac82f52f5f', { pageName: savedPage.name });
        } else if (wizardStep === 3) {
            return savedPage.name;
        } else if (wizardStep === 4) {
            return getText('msg_f1fbb2b43dca');
        }
    };

    const savePortalPage = triggerAction => {
        // triggerAction parameter can be integer 1 when function is triggered by "Save" button. Or integer 2, if function is triggered by "Next" or "Design" step click.
        // "save" will trigger the system to show a custom toast msg for success/error but not navigate anywhere.
        // "next" will trigger the system to show a custom toast msg for success/error and navigate to the next screen.

        if (!savedPage.name || savedPage.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return;
        }

        let portalPageInstance = {
            name: '',
            description: '',
            parentId: null,
            isLocked: false,
            isChild: true,
            isAdditionalUserVisible: false,
            status: 3,
            isTemplate: false,
            populationGroups: [],
            containers: [],
            scope: 'Client',
            agencyId: 0,
        };

        if (savedPage.name) {
            portalPageInstance.name = savedPage.name;
            // Add validation toast?
        }

        if (savedPage.displayName) {
            portalPageInstance.displayName = savedPage.displayName;
        }

        if (savedPage.description) {
            portalPageInstance.description = savedPage.description;
        }

        if (savedPage.parentId) {
            portalPageInstance.parentId = savedPage.parentId;
        }

        if (savedPage.isChild !== undefined) {
            portalPageInstance.isChild = savedPage.isChild;
        }

        if (savedPage.isLocked) {
            portalPageInstance.isLocked = savedPage.isLocked;
        }

        if (savedPage.isAdditionalUserVisible !== undefined) {
            portalPageInstance.isAdditionalUserVisible =
                savedPage.isAdditionalUserVisible;
        }

        if (savedPage.status) {
            portalPageInstance.status = savedPage.status;
        }

        if (savedPage.populationGroups) {
            portalPageInstance.populationGroups = savedPage.populationGroups;
        }

        if (savedPage.containers) {
            portalPageInstance.containers = savedPage.containers;
        }

        if (savedPage.agencyId) {
            portalPageInstance.agencyId = savedPage.agencyId;
        }

        if (savedPage.mobileConfiguration) {
            portalPageInstance.mobileConfiguration =
                savedPage.mobileConfiguration;
        }

        if (isAdminAudienceEnabled && savedPage.dataFilterId != null) {
            portalPageInstance.dataFilterId = savedPage.dataFilterId;
        }

        if (
            isAdminAudienceEnabled &&
            savedPage.id == null &&
            portalPageInstance.dataFilterId == null
        ) {
            portalPageInstance.dataFilterId = user?.jwt?.df;
        }

        if (location.state && location.state.loadFromMultiTenantTemplates) {
            if (savedPage.scope) {
                portalPageInstance.scope = savedPage.scope;
            } else {
                Toast.error(getText('msg_2ae81c4b87eb'));
                return;
            }

            portalPageInstance.tenantId = null;
            portalPageInstance.isChild = true;
            portalPageInstance.status = 1;
            portalPageInstance.isTemplate = true;
            portalPageInstance.availableTierIds =
                savedPage.availableTierIds ?? null;
            if (!location.state.loadFromTemplate) {
                portalPageInstance.containers = fetchedContainers;
                portalPageInstance.mobileConfiguration =
                    fetchedMobileConfiguration;
            }
        }

        if (location.state && location.state.loadFromClientTemplates) {
            portalPageInstance.isChild = true;
            portalPageInstance.status = 1;
            portalPageInstance.isTemplate = true;
            portalPageInstance.containers = fetchedContainers;
            portalPageInstance.mobileConfiguration = fetchedMobileConfiguration;
        }

        if (savedPage.id) {
            if (isActivePage && !hasDraft && isDirty && !savedPage.isTemplate) {
                return handleCreateDraftAndSave(
                    portalPageInstance,
                    triggerAction,
                );
            }

            if (isDirty) {
                portalPageInstance.id = savedPage.id;

                if (isDraftPage) {
                    portalPageInstance.publishDate = null;
                }

                // C) Pending portals: saving clears publish date and reverts to in-progress
                const isPendingPage = savedPage.status === 4;
                if (isPendingPage) {
                    portalPageInstance.status = 3;
                    portalPageInstance.publishDate = null;
                }

                // LayoutTab manages containers and mobileConfiguration directly
                delete portalPageInstance.containers;
                delete portalPageInstance.mobileConfiguration;

                return updatePortalPage(portalPageInstance)
                    .then(async response => {
                        // Always reload the portal page to get the latest updatedAt/updatedBy
                        const reloaded = await readPortalPage(
                            response.viewModels[0].id,
                            true,
                        );
                        const reloadedPage =
                            reloaded?.viewModels?.[0] || response.viewModels[0];
                        const normalizedReload = {
                            ...reloadedPage,
                            agencyId:
                                reloadedPage.agencyId ?? savedPage.agencyId,
                            scope: reloadedPage.scope ?? savedPage.scope,
                        };
                        setOriginalPage(normalizedReload);
                        setSavedPage(normalizedReload);

                        if (isDraftPage) {
                            setDraftHadPublishDate(false);
                            Toast.success(
                                getText('msg_4adce2882ebc'),
                            );
                        } else if (
                            isActivePage &&
                            hasDraft &&
                            draftHadPublishDate
                        ) {
                            setDraftHadPublishDate(false);
                            Toast.info(
                                getText('msg_efa6c224b965'),
                            );
                        } else if (isActivePage && hasDraft) {
                            Toast.success(
                                `Portal Page '${savedPage.name}' draft was updated successfully.`,
                            );
                        } else if (isPendingPage) {
                            Toast.info(
                                `Portal Page '${savedPage.name}' was updated and marked as In Progress. Assign a new publish date in the Publish tab.`,
                            );
                        } else {
                            Toast.success(
                                `Portal Page '${savedPage.name}' was updated successfully.`,
                            );
                        }

                        if (triggerAction === 2) {
                            handleOnClickNext();
                        } else if (triggerAction === 3) {
                            setWizardStep(3);
                        }
                    })
                    .catch(e => {
                        console.log(e);
                        const errors = e?.response?.data?.errors;
                        if (errors) {
                            for (const key in errors) {
                                if (errors.hasOwnProperty(key)) {
                                    const errorMessages = errors[key];
                                    errorMessages.forEach(errorMessage => {
                                        Toast.error(errorMessage);
                                    });
                                }
                            }
                        } else {
                            Toast.error(
                                getText('msg_77617a0c6af3'),
                                {},
                                getText('msg_456bc1bf0716'),
                            );
                        }
                    });
            } else {
                if (triggerAction === 1) {
                    Toast.warning(
                        getText('msg_46228aa300f7'),
                    );
                } else if (triggerAction === 2) {
                    handleOnClickNext();
                } else if (triggerAction === 3) {
                    setWizardStep(3);
                }
            }
        } else {
            return createPortalPage(portalPageInstance)
                .then(response => {
                    const responseData = response.viewModels[0];
                    const normalizedPage = {
                        ...responseData,
                        agencyId: responseData.agencyId ?? savedPage.agencyId,
                        scope: responseData.scope ?? savedPage.scope,
                    };
                    setOriginalPage(normalizedPage);
                    setSavedPage(normalizedPage);
                    Toast.success(
                        `Portal Page '${savedPage.name}' was saved successfully.`,
                    );
                    if (triggerAction === 2) {
                        handleOnClickNext();
                    } else if (triggerAction === 3) {
                        setWizardStep(3);
                    }
                })
                .catch(e => {
                    console.log(e);
                    const errors = e?.response?.data?.errors;
                    if (errors) {
                        for (const key in errors) {
                            if (errors.hasOwnProperty(key)) {
                                const errorMessages = errors[key];
                                errorMessages.forEach(errorMessage => {
                                    Toast.error(errorMessage);
                                });
                            }
                        }
                    } else {
                        Toast.error(
                            getText('msg_77617a0c6af3'),
                            {},
                            getText('msg_456bc1bf0716'),
                        );
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    async function handleCreateDraftAndSave(portalPageInstance, triggerAction) {
        try {
            const livePortalId = savedPage.draftOfPortalId || savedPage.id;
            const liveContainerIds = (fetchedContainers || []).map(c => c.id);
            const liveMobileConfig = fetchedMobileConfiguration || [];

            delete portalPageInstance.id;
            portalPageInstance.draftOfPortalId = livePortalId;
            portalPageInstance.containers = liveContainerIds;
            portalPageInstance.mobileConfiguration = liveMobileConfig;

            const createResponse = await createPortalPage(portalPageInstance);
            const createdDraft = createResponse.viewModels[0];

            setHasDraft(true);
            setDraftHadPublishDate(false);
            setOriginalPage(createdDraft);
            setSavedPage(createdDraft);
            Toast.success(
                getText('msg_e5c8c585d4a6'),
                {},
                getText('msg_298d4c6ac749'),
            );

            if (triggerAction === 2) {
                handleOnClickNext();
            } else if (triggerAction === 3) {
                setWizardStep(3);
            }
        } catch (e) {
            console.error('Error creating draft:', e);
            Toast.error(getText('msg_c95d55f85466'));
        }
    }

    function handleDraftCreatedFromLayout(draftData) {
        setLivePage({ ...savedPage });
        setHasDraft(true);
        setDraftHadPublishDate(false);
        setOriginalPage(draftData);
        setSavedPage(draftData);
        if (draftData.containers) {
            setFetchedContainers(draftData.containers);
        }
        if (draftData.mobileConfiguration) {
            setFetchedMobileConfiguration(draftData.mobileConfiguration);
        }
    }

    async function handleDiscardDraft() {
        if (isDraftPage) {
            try {
                await deletePortalPage(savedPage.id);
                Toast.success(getText('msg_2713c848ef6b'));
                navigate(ROUTES.PORTAL_PAGES);
            } catch (e) {
                console.error('Error deleting draft:', e);
                Toast.error(getText('msg_0cc4abeb10f4'));
            }
            return;
        }

        const livePortalId = savedPage.draftOfPortalId || livePage?.id;
        if (!livePortalId) return;

        try {
            await discardDraft(livePortalId);
            setHasDraft(false);
            setDraftHadPublishDate(false);
            Toast.success(getText('msg_2713c848ef6b'));
            await loadPortalPage();
        } catch (e) {
            console.error('Error discarding draft:', e);
            Toast.error(getText('msg_0cc4abeb10f4'));
        }
    }

    function confirmDiscardDraft() {
        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            subtitle: getText('msg_5f247b151ed5'),
            text: getText('msg_2c6d467777ef'),
            onYes: handleDiscardDraft,
            onNo: () => {},
            yesButtonText: getText('msg_965902973205'),
            noButtonText: getText('msg_77dfd2135f4d'),
        });
    }

    const handleOnClickNext = () => {
        setWizardStep(wizardStep + 1);
    };

    const handleOnClickPrevious = () => {
        setWizardStep(wizardStep - 1);
    };

    const handleSave = () => {
        if (wizardStep === 1) {
            //savePage();
            savePortalPage(1); // Call savePortalPage function and pass 1 as triggerAction, aka "Save" button
        } else {
            navigate(ROUTES.PORTAL_PAGES);
        }
    };

    const handleClose = () => {
        if (location.state && location.state.loadFromMultiTenantTemplates) {
            return navigate(ROUTES.TEMPLATES);
        }
        if (location.state && location.state.loadFromClientTemplates) {
            return navigate(ROUTES.PORTAL_TEMPLATES);
        }
        if (hasAttribute(SecurityAttributes.PortalsReadOnly)) {
            return navigate(ROUTES.PORTAL_PAGES);
        }

        if (isDirty && wizardStep === 1) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_03a2e016b500'),
                //onYes: () => { savePage(); navigate(ROUTES.PORTAL_PAGES) },
                onYes: () => {
                    savePortalPage(1);
                    navigate(ROUTES.PORTAL_PAGES);
                },
                onNo: () => navigate(ROUTES.PORTAL_PAGES),
                noButtonText: getText('msg_ed716b362356'),
                yesButtonText: getText('msg_7af7a6cd22d8'),
            });
        } else {
            navigate(ROUTES.PORTAL_PAGES);
        }
    };

    const updatePopulationGroups = newGroups => {
        let popGroups = newGroups;
        if (Array.isArray(newGroups)) {
            popGroups = newGroups.map(group => {
                return group.id;
            });
        }
        setSavedPage(prevState => ({
            ...prevState,
            populationGroups: popGroups,
        }));
    };

    const handleMobileChange = () => {
        setResolution(786);
    };

    const handleDesktopChange = () => {
        setResolution(1366 - SIDE_NAV_BAR_WIDTH);
    };

    const isLayout = () => {
        if (wizardStep === 2) {
            return true;
        } else {
            return false;
        }
    };

    const layoutTabRef = useRef();

    const handleAddButtonClicked = () => {
        if (layoutTabRef.current) {
            layoutTabRef.current.openAddContainerModal();
        }
    };

    const handleResetMobileLayoutButtonClicked = () => {
        if (layoutTabRef.current) {
            layoutTabRef.current.resetMobileLayout();
        }
    };

    const handleSaveLayoutButtonClicked = () => {
        if (layoutTabRef.current) {
            layoutTabRef.current.saveLayoutGrid();
        }
    };

    const [deletedContainers, setDeletedContainers] = useState([]);

    const [isSaving, setIsSaving] = useState(false);

    function areConfigEqual(arr1, arr2) {
        // Check if both arrays are the same length
        if (arr1.length !== arr2.length) {
            return false;
        }
        // Compare each element in the arrays
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) {
                return false;
            }
        }
        return true; // Return true if all elements are equal and in the same order
    }

    /*const handleSaveGrid = async () => {
    setIsSaving(true);

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    try {
      // Create a new variable for mobileConfiguration
      const newMobileLayout = [...savedMobileLayout];

      for (const container of savedGrid) {

        // Check if id is null, indicating a new container
        if (container.id === null) {
          // Remove the id property from the container
          delete container.id;

          // Create the container and wait for it to finish
          const createdContainer = await createContainer(container);

          // Find the index of the temporary id and replace it with the returned container's id
          const index = newMobileLayout.findIndex(id => id === container.temporaryId);
          if (index !== -1) {
            newMobileLayout[index] = createdContainer.viewModels[0].id; // Replace temporaryId with the new id
          }

          // Wait for 2 seconds before proceeding to the next operation
          await delay(2000);
        } else if (container.id === container.temporaryId) {
          // Remove the containerId from each element in containerAssets
          if (container.containerAssets) {
            for (const asset of container.containerAssets) {
              delete asset.id; // Remove containerId from each asset
            }
          }
          // Update the container and wait for it to finish
          await updateContainer(container);

          // Wait for 2 seconds before proceeding to the next operation
          await delay(2000);
        }
      }

      // Process deletions one at a time
      for (const containerId of deletedContainers) {
        await deleteContainer(containerId);
        // Wait for 2 seconds before proceeding to the next operation
        await delay(2000);
      }


      // Update portal mobile config only if it has changed from what's stored in db

      if (!areConfigEqual(newMobileLayout, fetchedMobileConfiguration)) {
        const updatedPage = {
          ...savedPage,
          mobileConfiguration: newMobileLayout // Use the new variable
        };
        await updatePortalPage(updatedPage);
        //setFetchedMobile(newMobileLayout);
      }

      Toast.success("All the changes were saved successfully.");
      setDeletedContainers([]);
      setSavedGrid([]);
      loadPortalPage();

    } catch (error) {
      Toast.error("There was an error saving the changes. Please, try again.");
    } finally {
      setIsSaving(false);
    }
  };*/

    const autoSaveRef = useRef(null); // Ref to store the interval ID

    const savedGridRef = useRef(savedGrid);

    const savedPageRef = useRef(savedPage);

    useEffect(() => {
        savedPageRef.current = savedPage;
    }, [savedPage]);

    const fetchedContainersRef = useRef(fetchedContainers); // Create a ref to track fetchedContainers

    useEffect(() => {
        fetchedContainersRef.current = fetchedContainers; // Update the ref whenever fetchedContainers changes
    }, [fetchedContainers]);

    const autoSaveGrid = async () => {
        if (isSaving) return; // If already saving, prevent from auto-saving

        setIsSaving(true);

        try {
            await handleSaveGrid(savedGridRef.current); // Use the ref to get the latest savedGrid
            Toast.info(getText('msg_3fc149881267'));
        } catch (error) {
            console.error('Error during auto-save:', error);
            Toast.error(
                getText('msg_5de2255fb804'),
            );
        } finally {
            setIsSaving(false);
        }
    };

    /*const handleSaveGrid = async () => {
    setIsSaving(true);

    try {
      const newMobileLayout = [...savedMobileLayout]; // Copy of the current mobile layout
      const fetchedContainerMap = new Map(fetchedContainers.map(container => [container.id, container])); // Map of fetched containers by ID

      // Determine which containers to create and update
      const containersToCreate = [];
      const containersToUpdate = [];

      // Create a set of IDs from the savedGrid for easy lookup
      const currentContainerIds = new Set(savedGrid.map(container => container.id));

      // Containers to create or update
      for (const container of savedGrid) {
        if (container.id === null) {
          // If new container
          console.log("added to create");
          containersToCreate.push(container);
        } else {
          // If existing container
          const existingContainer = fetchedContainerMap.get(container.id);
          if (existingContainer) {
            // Check if the container has changed
            delete container.temporaryId;
            if (JSON.stringify(existingContainer) !== JSON.stringify(container)) {
              console.log("added to update");
              containersToUpdate.push(container);
            }
          }
        }
      }

      // Containers to delete
      const containersToDelete = fetchedContainers
        .filter(container => !currentContainerIds.has(container.id)) // Find the containers not in the current grid
        .map(container => container.id); // Map to their IDs

      // Create new containers
      for (const container of containersToCreate) {
        let comparingContainer = container;
        delete comparingContainer.id; // Remove the id property for creation from this temporary comparison container
        const createdContainer = await createContainer(comparingContainer); // Create the container
        const index = newMobileLayout.findIndex(id => id === container.temporaryId);
        if (index !== -1) {
          newMobileLayout[index] = createdContainer.viewModels[0].id; // Replace temporaryId with the new id
        }
      }

      // Update existing containers
      for (const container of containersToUpdate) {
        await updateContainer(container); // Update the container
      }

      // Delete containers that are no longer present
      for (const containerId of containersToDelete) {
        await deleteContainer(containerId); // Delete the container
      }

      // Update portal mobile config only if it has changed from what's stored in db
      if (!areConfigEqual(newMobileLayout, fetchedMobileConfiguration)) {
        const updatedPage = {
          ...savedPage,
          mobileConfiguration: newMobileLayout // Update mobile configuration
        };
        await updatePortalPage(updatedPage); // Save the updated portal page
      }

      Toast.success("All the changes were saved successfully."); // Show success message

      // Refresh the fetched containers to ensure we have the latest data
      await loadPortalPage(); // Reload the portal page to get the latest containers

      // Clear local state
      setDeletedContainers([]); // Clear deleted containers state
      setSavedGrid([]); // Clear saved grid state

    } catch (error) {
      Toast.error("There was an error saving the changes. Please, try again."); // Show error message
    } finally {
      setIsSaving(false); // Reset saving state
    }
  };*/

    /*const handleSaveGrid = async () => {
    setIsSaving(true);

    try {
      const newMobileLayout = [...savedMobileLayout]; // Copy of the current mobile layout
      const fetchedContainerMap = new Map(fetchedContainers.map(container => [container.id, container])); // Map of fetched containers by ID

      // Determine which containers to create and update
      const containersToCreate = [];
      const containersToUpdate = [];

      // Create a set of IDs from the savedGrid for easy lookup
      const currentContainerIds = new Set(savedGrid.map(container => container.id));

      // Containers to create or update
      for (const container of savedGrid) {
        if (container.id === null) {
          // If new container
          console.log("added to create");
          containersToCreate.push(container);
        } else {
          // If existing container
          const existingContainer = fetchedContainerMap.get(container.id);
          if (existingContainer) {
            // Check if the container has changed
            delete container.temporaryId;
            if (JSON.stringify(existingContainer) !== JSON.stringify(container)) {
              console.log("added to update");
              containersToUpdate.push(container);
            }
          }
        }
      }

      // Containers to delete
      const containersToDelete = fetchedContainers
        .filter(container => !currentContainerIds.has(container.id)) // Find the containers not in the current grid
        .map(container => container.id); // Map to their IDs

      // Create new containers
      for (const container of containersToCreate) {
        delete container.id; // Remove the id property for creation
        const createdContainer = await createContainer(container); // Create the container
        const index = newMobileLayout.findIndex(id => id === container.temporaryId);
        if (index !== -1) {
          newMobileLayout[index] = createdContainer.viewModels[0].id; // Replace temporaryId with the new id
        }
      }

      // Update existing containers
      for (const container of containersToUpdate) {
        await updateContainer(container); // Update the container
      }

      // Delete containers that are no longer present
      for (const containerId of containersToDelete) {
        await deleteContainer(containerId); // Delete the container
      }

      // Update portal mobile config only if it has changed from what's stored in db
      if (!areConfigEqual(newMobileLayout, fetchedMobileConfiguration)) {
        const updatedPage = {
          ...savedPage,
          mobileConfiguration: newMobileLayout // Update mobile configuration
        };
        await updatePortalPage(updatedPage); // Save the updated portal page
      }

      Toast.success("All the changes were saved successfully."); // Show success message

      // Refresh the fetched containers to ensure we have the latest data
      await loadPortalPage(); // Reload the portal page to get the latest containers

      // Clear local state
      setDeletedContainers([]); // Clear deleted containers state
      setSavedGrid([]); // Clear saved grid state

    } catch (error) {
      Toast.error("There was an error saving the changes. Please, try again."); // Show error message
    } finally {
      setIsSaving(false); // Reset saving state
    }

  };*/

    /*const handleSaveGrid = async () => {
    setIsSaving(true);
  
    try {
      const newMobileLayout = [...savedMobileLayout]; // Copy of the current mobile layout
      const fetchedContainerMap = new Map(fetchedContainers.map(container => [container.id, container])); // Map of fetched containers by ID
  
      // Determine which containers to create and update
      const containersToCreate = [];
      const containersToUpdate = [];
  
      // Create a set of IDs from the savedGrid for easy lookup
      const currentContainerIds = new Set(savedGrid.map(container => container.id));
  
      // Containers to create or update
      for (const container of savedGrid) {
        if (container.id === null) {
          // If new container
          console.log("adding container to create, temp id: "+container.temporaryId);
          containersToCreate.push(container);
        } else {
          // If existing container
          const existingContainer = fetchedContainerMap.get(container.id);
          if (existingContainer) {
            // Check if the container has changed
            let temporaryContainerCopy = container;
            delete temporaryContainerCopy.temporaryId; // Remove temporaryId for comparison (the fetched ones don't have that temporaryId)
            if (JSON.stringify(existingContainer) !== JSON.stringify(temporaryContainerCopy)) {
              console.log("adding container to update, id: "+container.id);
              containersToUpdate.push(container);
            }
          }
        }
      }
  
      // Create new containers
      for (const container of containersToCreate) {
        delete container.id; // Remove the id property for creation
        const createdContainer = await createContainer(container); // Create the container
  
        // Update the savedGrid with the new container ID and temporaryId
        setSavedGrid(prevGrid => 
          prevGrid.map(item => 
            item.id === null // If id was null for the savedGrid container, replace with actual id on returned created object
              ? { ...item, id: createdContainer.viewModels[0].id, temporaryId: createdContainer.viewModels[0].id } 
              : item
          )
        );
  
        const index = newMobileLayout.findIndex(id => id === container.temporaryId);
        if (index !== -1) {
          newMobileLayout[index] = createdContainer.viewModels[0].id; // Replace temporaryId with the new id
        }
      }
  
      // Update existing containers
      for (const container of containersToUpdate) {
        await updateContainer(container); // Update the container
      }
  
      // Delete containers that are no longer present
      const containersToDelete = fetchedContainers
        .filter(container => !currentContainerIds.has(container.id)) // Find the containers not in the current grid
        .map(container => container.id); // Map to their IDs
  
      for (const containerId of containersToDelete) {
        await deleteContainer(containerId); // Delete the container
      }
  
      // Update portal mobile config only if it has changed from what's stored in db
      if (!areConfigEqual(newMobileLayout, fetchedMobileConfiguration)) {
        const updatedPage = {
          ...savedPage,
          mobileConfiguration: newMobileLayout // Update mobile configuration
        };
        await updatePortalPage(updatedPage); // Save the updated portal page
      }
  
      Toast.success("All the changes were saved successfully."); // Show success message
  
      // Clear local state
      setDeletedContainers([]); // Clear deleted containers state
      //setSavedGrid([]); // Clear saved grid state
  
    } catch (error) {
      Toast.error("There was an error saving the changes. Please, try again."); // Show error message
    } finally {
      setIsSaving(false); // Reset saving state
    }
  };*/

    /*const [savedGridCopy, setSavedGridCopy] = useState([]);

  const handleSaveGrid = async () => {
    console.log("entering handle save");
    console.log("saved grid is: "+JSON.stringify(savedGrid));
    setIsSaving(true);
  
    try {
      const newMobileLayout = [...savedMobileLayout]; // Copy of the current mobile layout
      const fetchedContainerMap = new Map(fetchedContainers.map(container => [container.id, container])); // Map of fetched containers by ID
      
      // Create a deep copy of savedGrid to avoid mutations during execution
      setSavedGridCopy(JSON.parse(JSON.stringify(savedGrid)));

      // Determine which containers to create and update
      const containersToCreate = [];
      const containersToUpdate = [];
  
      // Create a set of IDs from the savedGridCopy for easy lookup
      const currentContainerIds = new Set(savedGridCopy.map(container => container.id));
  
      // Containers to create or update
      for (const container of savedGridCopy) {
        if (container.id === null) {
          // If new container
          console.log("adding container to create, temp id: " + container.temporaryId);
          containersToCreate.push(container);
        } else {
          // If existing container
          const existingContainer = fetchedContainerMap.get(container.id);
          if (existingContainer) {
            // Check if the container has changed
            let temporaryContainerCopy = { ...container }; // Create a shallow copy for comparison
            delete temporaryContainerCopy.temporaryId; // Remove temporaryId for comparison (the fetched ones don't have that temporaryId)
            if (JSON.stringify(existingContainer) !== JSON.stringify(temporaryContainerCopy)) {
              console.log("adding container to update, id: " + container.id);
              containersToUpdate.push(container);
            }
          }
        }
      }
  
      // Create new containers
      for (const container of containersToCreate) {
        delete container.id; // Remove the id property for creation
        const createdContainer = await createContainer(container); // Create the container

        // Update the savedGrid with the returned container ID and update it in temporaryId as well
        setSavedGridCopy(prevGrid =>
          prevGrid.map(item =>
            item.temporaryId === container.temporaryId // If id was null for the savedGrid container, replace with actual id on returned created object
              ? { ...item, id: createdContainer.viewModels[0].id, temporaryId: createdContainer.viewModels[0].id }
              : item
          )
        );
  
        const index = newMobileLayout.findIndex(id => id === container.temporaryId);
        if (index !== -1) {
          newMobileLayout[index] = createdContainer.viewModels[0].id; // Replace temporaryId with the new id
        }
      }
  
      // Update existing containers
      for (const container of containersToUpdate) {
        await updateContainer(container); // Update the container
      }
  
      // Delete containers that are no longer present
      //TODO: Fix this, not working properly
      //const containersToDelete = fetchedContainers
        .filter(container => !currentContainerIds.has(container.id)) // Find the containers not in the current grid
        .map(container => container.id); // Map to their IDs
  
      for (const containerId of containersToDelete) {
        await deleteContainer(containerId); // Delete the container
      }
  
      // Update portal mobile config only if it has changed from what's stored in db
      if (!areConfigEqual(newMobileLayout, fetchedMobileConfiguration)) {
        const updatedPage = {
          ...savedPage,
          mobileConfiguration: newMobileLayout // Update mobile configuration
        };
        await updatePortalPage(updatedPage); // Save the updated portal page
      }
  
      Toast.success("All the changes were saved successfully."); // Show success message
  
      // Clear local state
      setDeletedContainers([]); // Clear deleted containers state
      setFetchedContainers(savedGridCopy);
  
    } catch (error) {
      Toast.error("There was an error saving the changes. Please, try again."); // Show error message
    } finally {
      setIsSaving(false); // Reset saving state
    }
};*/

    /*const handleSaveGrid = async () => {
    setIsSaving(true);

    try {
      const newMobileLayout = [...savedMobileLayout]; // Copy of the current mobile layout
      const fetchedContainerMap = new Map(fetchedContainers.map(container => [container.id, container])); // Map of fetched containers by ID

      // Determine which containers to create and update
      const containersToCreate = [];
      const containersToUpdate = [];

      // Create a set of IDs from the savedGrid for easy lookup
      const currentContainerIds = new Set(savedGrid.map(container => container.id));

      // Containers to create or update
      for (const container of savedGrid) {
        if (container.id === null) {
          // If new container
          console.log("added to create");
          containersToCreate.push(container);
        } else {
          // If existing container
          const existingContainer = fetchedContainerMap.get(container.id);
          if (existingContainer) {
            // Check if the container has changed
            delete container.temporaryId;
            if (JSON.stringify(existingContainer) !== JSON.stringify(container)) {
              console.log("added to update");
              containersToUpdate.push(container);
            }
          }
        }
      }

      // Containers to delete
      const containersToDelete = fetchedContainers
        .filter(container => !currentContainerIds.has(container.id)) // Find the containers not in the current grid
        .map(container => container.id); // Map to their IDs

      // Create new containers
      for (const container of containersToCreate) {
        let comparingContainer = container;
        delete comparingContainer.id; // Remove the id property for creation from this temporary comparison container
        const createdContainer = await createContainer(comparingContainer); // Create the container
        const index = newMobileLayout.findIndex(id => id === container.temporaryId);
        if (index !== -1) {
          newMobileLayout[index] = createdContainer.viewModels[0].id; // Replace temporaryId with the new id
        }
      }

      // Update existing containers
      for (const container of containersToUpdate) {
        await updateContainer(container); // Update the container
      }

      // Delete containers that are no longer present
      for (const containerId of containersToDelete) {
        await deleteContainer(containerId); // Delete the container
      }

      // Update portal mobile config only if it has changed from what's stored in db
      if (!areConfigEqual(newMobileLayout, fetchedMobileConfiguration)) {
        const updatedPage = {
          ...savedPage,
          mobileConfiguration: newMobileLayout // Update mobile configuration
        };
        await updatePortalPage(updatedPage); // Save the updated portal page
      }

      Toast.success("All the changes were saved successfully."); // Show success message

      // Refresh the fetched containers to ensure we have the latest data
      await loadPortalPage(); // Reload the portal page to get the latest containers

      // Clear local state
      setDeletedContainers([]); // Clear deleted containers state
      setSavedGrid([]); // Clear saved grid state

    } catch (error) {
      Toast.error("There was an error saving the changes. Please, try again."); // Show error message
    } finally {
      setIsSaving(false); // Reset saving state
    }
  };*/

    const handleSaveGrid = async savedGrid => {
        setIsSaving(true);

        try {
            const newMobileLayout = [...savedMobileLayout]; // Copy of the current mobile layout
            const fetchedContainerMap = new Map(
                fetchedContainersRef.current.map(container => [
                    container.id,
                    container,
                ]),
            ); // Map of fetched containers by ID

            // Determine which containers to create and update
            const containersToCreate = [];
            const containersToUpdate = [];

            // Create a set of IDs from the savedGrid for easy lookup
            const currentContainerIds = new Set(
                savedGrid.map(container => container.id),
            );

            // Containers to create or update
            for (const container of savedGrid) {
                if (container.id === null) {
                    // If new container
                    containersToCreate.push(container);
                } else {
                    // If existing container
                    const existingContainer = fetchedContainerMap.get(
                        container.id,
                    );
                    if (existingContainer) {
                        // Check if the container has changed
                        let temporaryContainerCopy = container;
                        delete temporaryContainerCopy.temporaryId; // Remove temporaryId for comparison (the fetched ones don't have that temporaryId)
                        if (
                            JSON.stringify(existingContainer) !==
                            JSON.stringify(temporaryContainerCopy)
                        ) {
                            containersToUpdate.push(container);
                        }
                    }
                }
            }

            // Containers to delete
            const containersToDelete = fetchedContainersRef.current
                .filter(container => !currentContainerIds.has(container.id)) // Find the containers not in the current grid
                .map(container => container.id); // Map to their IDs

            // Create new containers
            for (const container of containersToCreate) {
                let comparingContainer = container;
                delete comparingContainer.id; // Remove the id property for creation from this temporary comparison container
                const currentAgencyId = savedPageRef.current?.agencyId;
                if (currentAgencyId != null) {
                    comparingContainer.agencyId = currentAgencyId;
                }
                const createdContainer =
                    await createContainer(comparingContainer); // Create the container

                // Update the savedGrid with the returned container ID and update it in temporaryId as well
                setSavedGridCopy(prevGrid =>
                    prevGrid.map(item =>
                        item.temporaryId === container.temporaryId // If id was null for the savedGrid container, replace with actual id on returned created object
                            ? {
                                  ...item,
                                  id: createdContainer.viewModels[0].id,
                                  temporaryId:
                                      createdContainer.viewModels[0].id,
                              }
                            : item,
                    ),
                );

                const index = newMobileLayout.findIndex(
                    id => id === container.temporaryId,
                );
                if (index !== -1) {
                    newMobileLayout[index] = createdContainer.viewModels[0].id; // Replace temporaryId with the new id
                }
            }

            // Update existing containers
            for (const container of containersToUpdate) {
                await updateContainer(container); // Update the container
            }

            // Delete containers that are no longer present
            for (const containerId of containersToDelete) {
                await deleteContainer(containerId); // Delete the container
            }

            // Update portal mobile config only if it has changed from what's stored in db
            const updateMobileConfig = !areConfigEqual(
                newMobileLayout,
                fetchedMobileConfiguration,
            );
            if (updateMobileConfig) {
                const updatedPage = {
                    ...savedPage,
                    mobileConfiguration: newMobileLayout, // Update mobile configuration
                };
                await updatePortalPage(updatedPage); // Save the updated portal page
            }

            // After all save operations, always refresh containers
            await refreshContainers();

            Toast.success(
                isDraftPage
                    ? 'Draft updated. Assign a publish date in the Publish tab to schedule live update.'
                    : 'All the changes were saved successfully.',
            ); // Show success message

            // Clear local state
            setDeletedContainers([]); // Clear deleted containers state
        } catch (error) {
            Toast.error(
                getText('msg_8b2e411ee7fe'),
            ); // Show error message
        } finally {
            setIsSaving(false); // Reset saving state
        }
    };

    useEffect(() => {
        savedGridRef.current = savedGrid;
    }, [savedGrid]);

    const handlePublish = async () => {
        if (!isSaving) {
            const hasChanges = await layoutTabRef.current.checkForChanges();

            if (hasChanges) {
                showModal({
                    type: ModalTypes.ACTION,
                    title: getText('msg_b78130c92f32'),
                    subtitle: getText('msg_2f4e8fc9ac09'),
                    text: getText('msg_f3cefc556397'),
                    onYes: () => {
                        setWizardStep(3);
                    },
                    onNo: () => {},
                    yesButtonText: getText('msg_13234e3c5211'),
                    noButtonText: getText('msg_bd5f41d6a46f'),
                });
            } else {
                setWizardStep(3);
            }
        }
    };

    async function handlePreviewFromLayout() {
        if (isSaving) {
            return;
        }

        if (!layoutTabRef.current || !layoutTabRef.current.checkForChanges) {
            setWizardStep(4);
            return;
        }

        try {
            const hasChanges = await layoutTabRef.current.checkForChanges();

            if (!hasChanges) {
                setWizardStep(4);
                return;
            }
            if (!layoutTabRef.current) {
                setWizardStep(4);
                return;
            }

            const saveResult = await layoutTabRef.current.saveLayoutGrid(true);
            if (saveResult) {
                setWizardStep(4);
            }
        } catch (error) {
            console.error(
                'Error checking layout changes before preview:',
                error,
            );
            Toast.error(
                getText('msg_cd2b89c4ab51'),
            );
        }
    }

    async function handleAttributesFromLayout() {
        if (isSaving) {
            return;
        }

        if (!layoutTabRef.current || !layoutTabRef.current.checkForChanges) {
            setWizardStep(1);
            return;
        }

        try {
            const hasChanges = await layoutTabRef.current.checkForChanges();

            if (!hasChanges) {
                setWizardStep(1);
                return;
            }
            if (!layoutTabRef.current) {
                setWizardStep(1);
                return;
            }

            const saveResult = await layoutTabRef.current.saveLayoutGrid(true);
            if (saveResult) {
                setWizardStep(1);
            }
        } catch (error) {
            console.error(
                'Error checking layout changes before returning to attributes:',
                error,
            );
            Toast.error(
                getText('msg_0a5c75f6f2a7'),
            );
        }
    }

    return isLoading ? (
        <Spinner width={50} className={'loadingSpinner'} />
    ) : (
        <div
            className="page-content"
            style={{ height: wizardStep !== 1 ? '100%' : 'auto' }}
        >
            <div
                className="card"
                style={{ height: wizardStep !== 1 ? '100%' : 'auto' }}
            >
                <div
                    className="card-body px-0"
                    style={{
                        padding: '1.5rem 0px 0px 0px',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        overflow: 'auto',
                    }}
                >
                    {/* Page Header with Title and Action */}
                    {/* Page Title */}
                    {isLayout() ? (
                        <div className="d-flex justify-content-between align-items-center">
                            <span
                                onClick={handleAttributesFromLayout}
                                type="button"
                                className="ms-3 me-4  button-base ing-popup-blue-negative"
                            >
                                <i className="bi bi-chevron-left me-1" />
                                {getText('msg_a6652617f2c7')}
                            </span>

                            <div
                                style={{
                                    width: '2px',
                                    border: '1px solid #979797',
                                    height: '100%',
                                }}
                            ></div>

                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    width: 'fit-content',
                                    color:
                                        resolution > 786
                                            ? 'var(--iNGAGED-Primary-Color)'
                                            : '#2F2B43',
                                    cursor: 'pointer',
                                }}
                            >
                                <i
                                    className="bi bi-display me-1"
                                    style={{
                                        fontSize: '1.5rem',
                                    }}
                                ></i>
                                <p
                                    onClick={() => {
                                        if (!isSaving) {
                                            handleDesktopChange();
                                        }
                                    }}
                                    style={{
                                        fontWeight:
                                            resolution > 786 ? '600' : '500',
                                    }}
                                >
                                    {getText('msg_532c67fe1b5a')}
                                </p>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    width: 'fit-content',
                                    color:
                                        resolution <= 786
                                            ? 'var(--iNGAGED-Primary-Color)'
                                            : '#2F2B43',
                                    cursor: 'pointer',
                                }}
                            >
                                <i
                                    className="bi bi-phone me-1"
                                    style={{ fontSize: '1.5rem' }}
                                ></i>
                                <p
                                    onClick={() => {
                                        if (!isSaving) {
                                            handleMobileChange();
                                        }
                                    }}
                                    style={{
                                        fontWeight:
                                            resolution <= 786 ? '600' : '500',
                                    }}
                                >
                                    {getText('msg_b1d702459399')}
                                </p>
                            </div>

                            <div
                                style={{
                                    width: '2px',
                                    border: '1px solid #979797',
                                    height: '100%',
                                }}
                            ></div>

                            {resolution > 786 ? (
                                <div
                                    onClick={() => {
                                        if (!isSaving) {
                                            handleAddButtonClicked();
                                        }
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            backgroundColor:
                                                'var(--iNGAGED-Success-Color2)',
                                            borderRadius: '50%',
                                            width: '32px',
                                            height: '32px',
                                            color: 'white',
                                            fontSize: '1.2rem',
                                        }}
                                    >
                                        <i className="bi bi-plus-lg"></i>
                                    </div>
                                    <p
                                        style={{
                                            color: '#2F2B43',
                                            fontWeight: '500',
                                        }}
                                    >
                                        {getText('msg_61cc55aa0453')}
                                    </p>
                                </div>
                            ) : (
                                <div
                                    onClick={() => {
                                        if (!isSaving && savedGrid.length > 0) {
                                            handleResetMobileLayoutButtonClicked();
                                        }
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            backgroundColor: '#D9D9D9',
                                            borderRadius: '50%',
                                            width: '32px',
                                            height: '32px',
                                            color: 'white',
                                            fontSize: '1.2rem',
                                        }}
                                    >
                                        <i className="bi bi-plus-lg"></i>
                                    </div>
                                    <p
                                        style={{
                                            color: '#2F2B43',
                                            fontWeight: '500',
                                        }}
                                    >
                                        {getText('msg_44c57abd888a')}
                                    </p>
                                </div>
                            )}
                            <div
                                style={{
                                    width: '2px',
                                    border: '1px solid #979797',
                                    height: '100%',
                                }}
                            ></div>

                            {location.state &&
                            (location.state.loadFromMultiTenantTemplates ||
                                location.state.loadFromClientTemplates) ? (
                                <span
                                    onClick={() => {
                                        if (!isSaving) {
                                            handleSaveLayoutButtonClicked();
                                        }
                                    }}
                                    type="button"
                                    className="ms-3 me-4 ing-popup-blue-positive"
                                >
                                    {getText('msg_efc007a393f6')}
                                </span>
                            ) : (
                                <>
                                    {((isActivePage && hasDraft) ||
                                        isDraftPage) &&
                                        layoutSource !== 'live' && (
                                            <span
                                                onClick={() => {
                                                    if (!isSaving) {
                                                        confirmDiscardDraft();
                                                    }
                                                }}
                                                type="button"
                                                className="ms-3 me-2 ing-text-buttons"
                                                style={{
                                                    cursor: 'pointer',
                                                    color: '#EF4E45',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                }}
                                            >
                                                <i className="bi bi-trash3" />
                                                {getText('msg_965902973205')}
                                            </span>
                                        )}
                                    {layoutSource !== 'live' && (
                                        <span
                                            onClick={() => {
                                                if (!isSaving) {
                                                    /*handleSaveGrid(savedGrid)*/ handleSaveLayoutButtonClicked();
                                                }
                                            }}
                                            type="button"
                                            className="ms-3 me-4 ing-text-buttons-save"
                                            style={{
                                                cursor: 'pointer',
                                                color: 'rgba(102, 112, 133, 1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                            }}
                                        >
                                            <i
                                                className="bi bi-check2"
                                                style={{ fontSize: '1.2rem' }}
                                            />
                                            {getText('msg_efc007a393f6')}
                                        </span>
                                    )}

                                    <span
                                        onClick={handlePreviewFromLayout}
                                        type="button"
                                        className="ms-3 me-4 ing-popup-blue-positive"
                                    >
                                        {getText('msg_f1fbb2b43dca')}
                                        <i className="bi bi-chevron-right" />
                                    </span>
                                </>
                            )}
                        </div>
                    ) : (
                        <div
                            className="d-flex align-items-center"
                            style={{
                                width: '100%',
                                padding: '0px 20px',
                                justifyContent: 'space-between',
                            }}
                        >
                            {/* Page Title and Preview mode toggle */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '360px',
                                }}
                            >
                                <p
                                    className="page-title"
                                    style={{
                                        width: 'auto',
                                        marginLeft: '16px',
                                        marginBottom: 0,
                                    }}
                                >
                                    {renderPageTitle()}
                                </p>

                                {wizardStep === 4 && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            marginLeft: '40px',
                                            gap: '12px',
                                        }}
                                    >
                                        {((isActivePage && hasDraft) ||
                                            isDraftPage) && (
                                            <>
                                                <CompactDropdown
                                                    defaultValue={
                                                        previewSource ===
                                                        'draft'
                                                            ? 'Draft'
                                                            : 'Live'
                                                    }
                                                    options={[
                                                        {
                                                            value: 'draft',
                                                            label: getText('msg_23d33e22acfc'),
                                                        },
                                                        {
                                                            value: 'live',
                                                            label: getText('msg_65c821a596ce'),
                                                        },
                                                    ]}
                                                    onChange={setPreviewSource}
                                                />
                                                <div
                                                    style={{
                                                        width: '1px',
                                                        height: '24px',
                                                        backgroundColor:
                                                            '#979797',
                                                    }}
                                                />
                                            </>
                                        )}
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                color:
                                                    previewMode === 'desktop'
                                                        ? 'var(--iNGAGED-Primary-Color)'
                                                        : '#2F2B43',
                                            }}
                                            onClick={() =>
                                                setPreviewMode('desktop')
                                            }
                                        >
                                            <i
                                                className="bi bi-display me-1"
                                                style={{
                                                    fontSize: '1rem',
                                                }}
                                            />
                                            <p
                                                style={{
                                                    fontWeight:
                                                        previewMode ===
                                                        'desktop'
                                                            ? '600'
                                                            : '500',
                                                    marginBottom: 0,
                                                }}
                                            >
                                                {getText('msg_532c67fe1b5a')}
                                            </p>
                                        </div>

                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                color:
                                                    previewMode === 'mobile'
                                                        ? 'var(--iNGAGED-Primary-Color)'
                                                        : '#2F2B43',
                                            }}
                                            onClick={() =>
                                                setPreviewMode('mobile')
                                            }
                                        >
                                            <i
                                                className="bi bi-phone me-1"
                                                style={{
                                                    fontSize: '1rem',
                                                }}
                                            />
                                            <p
                                                style={{
                                                    fontWeight:
                                                        previewMode === 'mobile'
                                                            ? '600'
                                                            : '500',
                                                    marginBottom: 0,
                                                }}
                                            >
                                                {getText('msg_b1d702459399')}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Page Breadcrumbs */}
                            <div className="flex-grow-1 d-flex justify-content-center mx-4">
                                <p
                                    style={{
                                        fontSize: '16px',
                                        fontWeight: '400',
                                        color: 'rgba(102, 112, 133, 1)',
                                    }}
                                >
                                    <span
                                        className={`clickable ${wizardStep === 1 ? 'highlighted-step' : ''}`}
                                        onClick={() => {
                                            setWizardStep(1);
                                        }}
                                    >
                                        {getText('msg_a6652617f2c7')}
                                    </span>
                                    <i
                                        className="bi bi-chevron-right"
                                        style={{
                                            fontSize: '0.8em',
                                            marginLeft: '10px',
                                            marginRight: '10px',
                                        }}
                                    />
                                    <span
                                        className={`clickable ${wizardStep === 2 ? 'highlighted-step' : ''}`}
                                        onClick={() => {
                                            wizardStep === 1
                                                ? savePortalPage(2)
                                                : setWizardStep(2);
                                        }}
                                    >
                                        {getText('msg_59b035367211')}
                                    </span>
                                    <i
                                        className="bi bi-chevron-right"
                                        style={{
                                            fontSize: '0.8em',
                                            marginLeft: '10px',
                                            marginRight: '10px',
                                        }}
                                    />
                                    <span
                                        className={`clickable ${wizardStep === 4 ? 'highlighted-step' : ''}`}
                                        onClick={() => {
                                            setWizardStep(4);
                                        }}
                                    >
                                        {getText('msg_f1fbb2b43dca')}
                                    </span>
                                    {!location.state
                                        ?.loadFromMultiTenantTemplates &&
                                        !location.state
                                            ?.loadFromClientTemplates && (
                                            <>
                                                <i
                                                    className="bi bi-chevron-right"
                                                    style={{
                                                        fontSize: '0.8em',
                                                        marginLeft: '10px',
                                                        marginRight: '10px',
                                                    }}
                                                />
                                                <span
                                                    className={`clickable ${wizardStep === 3 ? 'highlighted-step' : ''}`}
                                                    onClick={async () => {
                                                        // Always fetch latest containers before checking for overlap
                                                        try {
                                                            const fetchedPortalPage =
                                                                await readPortalPage(
                                                                    savedPage.id ||
                                                                        location
                                                                            .state
                                                                            .id,
                                                                    true,
                                                                );
                                                            const containers =
                                                                fetchedPortalPage.containers ||
                                                                [];
                                                            if (
                                                                hasContainerOverlap(
                                                                    containers,
                                                                )
                                                            ) {
                                                                Toast.warning(
                                                                    getText('msg_4cbaf77a4432'),
                                                                    {},
                                                                    getText('msg_74b3589e00a8'),
                                                                );
                                                                return;
                                                            }
                                                            savePortalPage(3);
                                                        } catch (e) {
                                                            Toast.error(
                                                                getText('msg_88d0ce1116a9'),
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {getText('msg_5656400564f2')}
                                                </span>
                                            </>
                                        )}
                                </p>
                            </div>

                            {/* Page Actions */}
                            <div
                                className="d-flex align-items-center"
                                style={{
                                    width: '360px',
                                    marginRight: '16px',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                {((isActivePage && hasDraft) || isDraftPage) &&
                                    (wizardStep !== 4 ||
                                        (wizardStep === 4 &&
                                            previewSource !== 'live')) && (
                                        <p
                                            type="button"
                                            onClick={confirmDiscardDraft}
                                            className="ing-text-buttons me-3"
                                            style={{
                                                color: '#EF4E45',
                                            }}
                                        >
                                            <i className="bi bi-trash3 me-2" />
                                            {getText('msg_965902973205')}
                                        </p>
                                    )}
                                {!hasAttribute(
                                    SecurityAttributes.PortalsReadOnly,
                                ) && (
                                    <p
                                        type="button"
                                        onClick={handleSave}
                                        className="ing-text-buttons me-3"
                                        style={{
                                            display:
                                                wizardStep === 3 ||
                                                wizardStep === 4
                                                    ? 'none'
                                                    : 'inline',
                                        }}
                                    >
                                        <i className="bi bi-check-lg me-2" />
                                        {getText('msg_efc007a393f6')}
                                    </p>
                                )}
                                <p
                                    type="button"
                                    onClick={handleClose}
                                    className="ing-text-buttons d-inline ms-3"
                                >
                                    <i className="bi bi-x me-2" />
                                    {getText('msg_bbfa773e5a63')}
                                </p>
                            </div>
                        </div>
                    )}

                    <hr style={{ margin: '8px 0px 0px 0px' }} />

                    {wizardStep === 1 && (
                        <AttributesTab
                            savedPage={savedPage}
                            savePage={savePortalPage}
                            setSavedPage={setSavedPage}
                            updatePopulationGroups={updatePopulationGroups}
                            handleOnClickNext={handleOnClickNext}
                            loadFromMultiTenantTemplates={
                                location.state?.loadFromMultiTenantTemplates ||
                                false
                            }
                            loadFromClientTemplates={
                                location.state?.loadFromClientTemplates || false
                            }
                            isActivePage={isActivePage}
                            hasDraft={hasDraft}
                        ></AttributesTab>
                    )}
                    {wizardStep === 2 && (
                        <>
                            {isSaving && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        height: '100%',
                                        zIndex: '9999',
                                        background: '#FFFFFF70',
                                    }}
                                >
                                    <Spinner width={50} />
                                </div>
                            )}
                            <LayoutTab
                                key={`layout-${layoutSource}`}
                                ref={layoutTabRef}
                                selectedResolution={resolution}
                                portalId={
                                    layoutSource === 'live'
                                        ? savedPage.draftOfPortalId
                                        : savedPage.id || location.state.id
                                }
                                savedPage={savedPage}
                                savePage={setSavedPage}
                                fetchedContainers={fetchedContainers}
                                fetchedMobileConfiguration={
                                    fetchedMobileConfiguration
                                }
                                saveGrid={setSavedGrid}
                                saveMobileLayout={setSavedMobileLayout}
                                deleteContainers={setDeletedContainers}
                                handleOnClickNext={handleOnClickNext}
                                handleOnClickPrevious={handleOnClickPrevious}
                                onOverlapChange={setHasOverlap}
                                isActivePage={isActivePage}
                                hasDraft={hasDraft}
                                onDraftCreated={handleDraftCreatedFromLayout}
                            ></LayoutTab>
                        </>
                    )}
                    {wizardStep === 4 && (
                        <PreviewTab
                            savedPage={
                                previewSource === 'live' && livePage
                                    ? livePage
                                    : savedPage
                            }
                            previewMode={previewMode}
                        />
                    )}
                    {wizardStep === 3 && (
                        <>
                            <PublishTab
                                savedPage={savedPage}
                                handleOnClickPrevious={handleOnClickPrevious}
                                isActivePage={isActivePage}
                                hasDraft={hasDraft}
                                livePage={livePage}
                                onDiscardDraft={confirmDiscardDraft}
                                hasOverlap={hasOverlap}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewPortalScreen;
